import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import CustomTable from "../../../../../../CustomHeadlessTable/CustomTable";
import {
  getMaterialUsedManufacturingList,
  addMaterialUsedManufacturingToProduct,
  updateMaterialUsedManufacturing,
  deleteMaterialUsedManufacturing,
} from "../../../../../../../apis/Product/MaterialUsedManufacturingApi";
// import { getProdManufacturingMaterialDropdown } from "../../../../../../../apis/Dropdown";
import CustomInputComponent from "../../../../../../CustomHeadlessTable/components/CustomInputComponent";
import useBonsaiData from "../../../../../../../hooks/useBonsaiData";

const ProdMaterialUsedTable = ({ productId }) => {
  const { t } = useTranslation();
  const [data, setData] = useState([]);
  const [hiddenColumns, setHiddenColumns] = useState([]);
  const [unitMaterials, setUnitMaterials] = useState({});

  // Use cached Bonsai data instead of individual API call
  const { data: dropdownUsedFor, isLoading: isBonsaiLoading } = useBonsaiData();

  const toggleColumnVisibility = useCallback((columnId) => {
    setHiddenColumns((prev) =>
      prev.includes(columnId)
        ? prev.filter((id) => id !== columnId)
        : [...prev, columnId]
    );
  }, []);

  const fetchData = useCallback(() => {
    getMaterialUsedManufacturingList({
      productId,
      setObjects: setData,
    });
  }, [productId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (data.length > 0) {
      const newUnitMaterials = {};
      data.forEach((row) => {
        const materialValue = row["material"]?.value || row.material;
        if (materialValue) {
          const selectedMaterial = dropdownUsedFor.find(
            (option) => option?.value === materialValue
          );
          if (selectedMaterial) {
            newUnitMaterials[row.id] = selectedMaterial.unit;
          }
        }
      });
      setUnitMaterials(newUnitMaterials);
    }
  }, [data, dropdownUsedFor]);

  const dynamicPlaceholders = useMemo(
    () => ({
      material: {
        targetField: "qty",
        getPlaceholder: (
          selectedValue,
          dropdownData,
          rowData,
          rowId = "new"
        ) => {
          const selectedMaterial = dropdownData?.find(
            (option) => option?.value === selectedValue
          );
          if (selectedMaterial) {
            // For existing rows
            if (rowId !== "new") {
              setUnitMaterials((prev) => ({
                ...prev,
                [rowId]: selectedMaterial.unit,
              }));
            } else {
              setUnitMaterials((prev) => ({
                ...prev,
                new: selectedMaterial.unit, // Use a special key for new rows
              }));
            }
          }
        },
        dropdownDataKey: "material",
      },
    }),
    []
  );
  const handleEdit = useCallback(
    (editedItem) => {
      const foundProduct = dropdownUsedFor?.find(
        (option) => option.name === editedItem.material.value
      );
      const product_code = foundProduct
        ? foundProduct["code"]
        : editedItem.product_code;
      updateMaterialUsedManufacturing({
        productId,
        componentId: editedItem.id,
        ...editedItem,
        material: editedItem.material.value,
        objects: data,
        setObjects: setData,
        use_swal: true,
        product_code: product_code,
      });
    },
    [productId, data, setData, dropdownUsedFor]
  );

  const handleAdd = useCallback(
    (newItem) => {
      const foundProduct = dropdownUsedFor?.find(
        (option) => option.name === newItem.material.value
      );

      const product_code = foundProduct ? foundProduct["code"] : "";
      addMaterialUsedManufacturingToProduct({
        productId,
        ...newItem,
        material: newItem.material.value,
        callback: fetchData,
        use_swal: true,
        product_code: product_code,
      });
    },
    [productId, fetchData, dropdownUsedFor]
  );

  const handleDelete = useCallback(
    (itemToDelete) => {
      deleteMaterialUsedManufacturing({
        productId,
        componentId: itemToDelete.id,
        callback: fetchData,
      });
    },
    [productId, fetchData]
  );

  const columns = useMemo(
    () => [
      {
        accessor: "material",
        header: `${t("MATERIAL_SINGULAR")}*`,
        type: "dropdown",
        dropdownData: dropdownUsedFor,
        disableSortBy: true,
      },
      {
        accessor: "qty",
        header: t("QTY_FUNC_UNIT"),
        type: "number",
        tipHeader: "Tip",
        tipBody: `${t(
          "TIP_QTY_FUNC_MANUFACTURING"
        )} Some selected items do not have units; "unit" will be shown as a placeholder.`,
        disableSortBy: true,
        placeholder: "0",
        width: "120px",
        unit: (rowId, rowData) => {
          const rowType =
            rowData?.material?.value || rowData?.["material"]?.value;
          return rowType
            ? dropdownUsedFor.find((option) => option.value === rowType)?.unit
            : unitMaterials[rowId] || "unit";
        },
      },
      {
        accessor: "manuel",
        header: (props) => {
          const rowId = props.row?.original?.id;
          return `${t("Manual kgCO₂")} /${unitMaterials[rowId] || "unit"}`;
        },
        type: "number",
        tipHeader: "Tip",
        tipBody: `${t(
          "TIP_MANUEL_MANUFACTURING"
        )} Some selected items do not have units; "unit" will be shown as a placeholder.`,
        disableSortBy: true,
        placeholder: "0",
        width: "120px",
        unit: (rowId, rowData) => {
          const rowType = rowData?.type?.value || rowData?.["material"]?.value;
          return rowType
            ? "kgCO₂/" +
                dropdownUsedFor.find((option) => option.value === rowType)?.unit
            : unitMaterials[rowId]
            ? "kgCO₂/" + unitMaterials[rowId]
            : "kgCO₂/unit";
        },
        optional: true,
      },
      {
        accessor: "spacer1",
        header: "",
        type: "spacer",
        width: "40px",
        optional: true,
        disableSortBy: true,
      },
      {
        accessor: "carbon",
        header: t("CARBON_OUTPUT"),
        type: "custom",
        canSort: true,
        cell: ({ value }) => {
          return <CustomInputComponent value={value} readOnly />;
        },
        optional: true,
        subtotalUnit: "tCO₂e",
        subtotalFixed: true,
        width: "120px",
      },
    ],
    [dropdownUsedFor, t, unitMaterials]
  );

  const disableRules = {
    carbon: () => true,
  };

  const subtotalColumns = {
    carbon: { label: t("TOTAL_CARBON") },
  };

  const memoizedDropdownData = useMemo(
    () => ({
      material: dropdownUsedFor,
    }),
    [dropdownUsedFor]
  );

  return (
    <CustomTable
      columns={columns}
      data={data}
      setData={setData}
      onAdd={handleAdd}
      onEdit={handleEdit}
      onDelete={handleDelete}
      dropdownData={memoizedDropdownData}
      subtotalColumns={subtotalColumns}
      customInputs={{ carbon: CustomInputComponent }}
      hiddenColumns={hiddenColumns}
      toggleColumnVisibility={toggleColumnVisibility}
      disableRules={disableRules}
      dynamicPlaceholders={dynamicPlaceholders}
    />
  );
};

export default ProdMaterialUsedTable;
